/**
 * Excel Context Service — Office.js helpers for reading/writing spreadsheet data.
 */

import {validateScript} from "./scriptValidation";

/**
 * Whether the current host supports the Excel JS API at all. False when
 * running outside Excel (e.g. in a browser during development) or when the
 * host hasn't finished initialising — calling Excel.run in those cases throws
 * "Invalid API call in the current context".
 */
function isExcelHost(): boolean {
  try {
    return (
      typeof Excel !== "undefined" &&
      typeof Excel.run === "function" &&
      Office?.context?.host === Office.HostType.Excel
    );
  } catch {
    return false;
  }
}

/**
 * Runs `fn` inside Excel.run, but only after confirming the host is Excel.
 * Throws a descriptive error instead of the opaque "Invalid API call in the
 * current context" when called from a non-Excel host.
 */
async function runInExcel<T>(
  fn: (context: Excel.RequestContext) => Promise<T>,
): Promise<T> {
  if (!isExcelHost()) {
    throw new Error(
      "This add-in must be opened inside Microsoft Excel to use workbook features.",
    );
  }
  return Excel.run(fn);
}

export interface ExcelSettings {
  /** Display language, e.g. "pt-PT", "en-US" */
  displayLanguage: string;
  /** decimal separator used in the user's Excel: "." or "," */
  decimalSeparator: string;
  /** list/argument separator used in the user's Excel: "," or ";" */
  listSeparator: string;
}

export type ContextDegradationKind =
  | "context_truncated"
  | "context_read_failure";

export interface ContextDegradation {
  kind: ContextDegradationKind;
  originalSize?: number;
  retainedSize?: number;
}

/**
 * The workbook's coarse size, at the moment its context was read — sheet
 * count, used-range cell count and the byte size of what was actually sent
 * to the model. Counts and sizes, never sheet names, workbook names, cell
 * values or formulas (issue #58 / ADR-0002).
 */
export interface WorkbookShape {
  sheetCount: number;
  cellCount: number;
  snapshotBytes: number;
}

export interface ContextDataResult {
  data: string;
  degradation?: ContextDegradation;
  shape: WorkbookShape;
}

export interface ExcelSettingsResult {
  settings: ExcelSettings;
  readFailed: boolean;
}

/**
 * Reads the host application's locale settings so the LLM can produce
 * locale-aware formulas and number formats.
 */
export async function getExcelSettings(): Promise<ExcelSettings> {
  return (await getExcelSettingsWithDiagnostics()).settings;
}

export async function getExcelSettingsWithDiagnostics(): Promise<ExcelSettingsResult> {
  const displayLanguage = (() => {
    try {
      return Office?.context?.displayLanguage ?? navigator.language ?? "en-US";
    } catch {
      return navigator.language ?? "en-US";
    }
  })();

  // Excel JS API exposes Application.decimalSeparator / thousandsSeparator
  // in requirement set ExcelApi 1.11+
  let decimalSeparator = ".";
  let listSeparator = ",";

  let readFailed = false;
  try {
    await runInExcel(async (context) => {
      const app = context.workbook.application;
      app.load(["decimalSeparator", "thousandsSeparator"]);
      await context.sync();

      if (app.decimalSeparator) {
        decimalSeparator = app.decimalSeparator;
      }
      // When the decimal separator is "," the list/formula-argument separator
      // is always ";" and vice-versa.
      listSeparator = decimalSeparator === "," ? ";" : ",";
    });
  } catch {
    readFailed = true;
    // Older hosts may not support these properties — fall back to
    // inferring from the display language.
    const lang = displayLanguage.toLowerCase();
    const commaDecimalLocales = [
      "pt",
      "fr",
      "de",
      "es",
      "it",
      "nl",
      "pl",
      "cs",
      "da",
      "fi",
      "el",
      "hu",
      "no",
      "ro",
      "sk",
      "sl",
      "sv",
      "tr",
      "ru",
      "uk",
      "bg",
      "hr",
      "sr",
      "id",
      "vi",
    ];
    if (commaDecimalLocales.some((l) => lang.startsWith(l))) {
      decimalSeparator = ",";
      listSeparator = ";";
    }
  }

  return {
    settings: {displayLanguage, decimalSeparator, listSeparator},
    readFailed,
  };
}

/** Shown in place of a file name for a workbook that has never been saved. */
export const UNSAVED_WORKBOOK_NAME = "Unsaved workbook";

/**
 * The workbook's file name, used to tag chat threads. Office only exposes the
 * document URL, which is empty until the file is saved and changes whenever it
 * is renamed or moved — fine as a label, unusable as an identity.
 */
export function getWorkbookName(): string {
  let url: string | undefined;
  try {
    url = Office?.context?.document?.url;
  } catch {
    url = undefined;
  }
  if (!url) return UNSAVED_WORKBOOK_NAME;

  const path = url.split(/[?#]/)[0];
  const fileName = path.split(/[/\\]/).pop() ?? "";
  if (!fileName) return UNSAVED_WORKBOOK_NAME;

  try {
    return decodeURIComponent(fileName);
  } catch {
    return fileName;
  }
}

export async function getSelectedData(): Promise<string> {
  return runInExcel(async (context) => {
    const range = context.workbook.getSelectedRange();
    range.load("values");
    await context.sync();
    return valuesToCsv(range.values);
  });
}

export async function getUsedRangeData(sheetName?: string): Promise<string> {
  return runInExcel(async (context) => {
    const sheet = sheetName
      ? context.workbook.worksheets.getItem(sheetName)
      : context.workbook.worksheets.getActiveWorksheet();
    const range = sheet.getUsedRange();
    range.load("values");
    await context.sync();
    return valuesToCsv(range.values);
  });
}

export async function getContextData(): Promise<string> {
  return (await getContextDataWithDiagnostics()).data;
}

/** Reads workbook context and reports prompt-size degradation without values. */
export async function getContextDataWithDiagnostics(): Promise<ContextDataResult> {
  // Send ALL sheets so the LLM has full workbook context (cross-sheet refs, etc.).
  // Also include the active sheet name and current selection hint.
  return runInExcel(async (context) => {
    const parts: string[] = [];
    let readFailed = false;

    // Read the current selection address
    try {
      const selected = context.workbook.getSelectedRange();
      selected.load(["address", "rowCount", "columnCount"]);
      await context.sync();
      const multiCell = selected.rowCount > 1 || selected.columnCount > 1;
      if (multiCell) {
        parts.push(`[User has selected: ${selected.address}]`);
      }
    } catch {
      // ignore — selection info is optional
    }

    // Read active sheet name
    const activeSheet = context.workbook.worksheets.getActiveWorksheet();
    activeSheet.load("name");
    const sheets = context.workbook.worksheets;
    sheets.load("items/name");
    await context.sync();

    parts.push(`[Active sheet: ${activeSheet.name}]`);

    // Coarse workbook shape, gathered alongside the data itself rather than
    // read back out of it — a byte count is exact regardless of how the CSV
    // renders, and it never touches a cell value or sheet name (issue #58).
    let cellCount = 0;

    // Iterate all sheets and collect a layout summary (used data range +
    // existing chart positions) followed by the cell data. The layout summary
    // lets the LLM see what's already on the sheet so it can place new charts
    // in free space instead of on top of data or other charts.
    for (const sheet of sheets.items) {
      const used = sheet.getUsedRangeOrNullObject(true /* valuesOnly */);
      used.load(["address", "isNullObject", "rowCount", "columnCount"]);
      const charts = sheet.charts;
      charts.load([
        "items/name",
        "items/top",
        "items/left",
        "items/width",
        "items/height",
      ]);
      sheet.load("standardHeight");
      await context.sync();

      const bodyParts: string[] = [];

      if (!used.isNullObject) {
        cellCount += used.rowCount * used.columnCount;
        bodyParts.push(`[Used data range: ${stripSheetPrefix(used.address)}]`);

        // Office.js caps a single response's payload size. Loading values and
        // formulas separately — and catching each independently — means a
        // sheet too large for one of them still surfaces the other, and a
        // sheet too large for both still doesn't blank out the rest of the
        // workbook's context.
        try {
          used.load("values");
          await context.sync();
          const csv = valuesToAddressedCsv(used.values, used.address);
          if (csv) bodyParts.push(csv);
        } catch (err) {
          readFailed = true;
          console.warn(`Failed to read values for sheet "${sheet.name}":`, err);
          bodyParts.push(
            `[This sheet's data (${used.rowCount} rows × ${used.columnCount} columns) is too large to include here. Use a run_script query to read a narrower range instead.]`,
          );
        }

        try {
          used.load("formulas");
          await context.sync();
          const formulaList = formulasListing(used.formulas, used.address);
          if (formulaList) bodyParts.push(formulaList);
        } catch (err) {
          readFailed = true;
          console.warn(
            `Failed to read formulas for sheet "${sheet.name}":`,
            err,
          );
          // Formulas are supplementary — the CSV above (if it loaded) still stands.
        }
      }

      const chartSummary = summarizeCharts(charts.items, sheet.standardHeight);
      if (chartSummary) {
        bodyParts.push(chartSummary);
      }

      if (bodyParts.length > 0) {
        parts.push(`\n### Sheet: ${sheet.name}\n${bodyParts.join("\n")}`);
      }
    }

    const shapeOf = (data: string): WorkbookShape => ({
      sheetCount: sheets.items.length,
      cellCount,
      snapshotBytes: byteLength(data),
    });

    if (parts.length <= 1) {
      // Only the active sheet hint, no data
      const data = "";
      return {
        data,
        shape: shapeOf(data),
        ...(readFailed
          ? {degradation: {kind: "context_read_failure" as const}}
          : {}),
      };
    }

    const output = parts.join("\n");

    // Cap at ~100KB to avoid oversized prompts
    if (output.length > 100_000) {
      const data = output.slice(0, 100_000) + "\n... [data truncated]";
      return {
        data,
        shape: shapeOf(data),
        degradation: {
          kind: "context_truncated",
          originalSize: output.length,
          retainedSize: data.length,
        },
      };
    }
    return {
      data: output,
      shape: shapeOf(output),
      ...(readFailed
        ? {degradation: {kind: "context_read_failure" as const}}
        : {}),
    };
  });
}

export async function writeToRange(
  startCell: string,
  values: (string | number | boolean)[][],
): Promise<void> {
  await runInExcel(async (context) => {
    const {sheetName, ref} = parseSheetRef(startCell);
    const sheet = getSheet(context, sheetName);
    const rows = values.length;
    const cols = values[0]?.length ?? 0;
    if (rows === 0 || cols === 0) return;

    const endCell = offsetCell(ref, rows - 1, cols - 1);
    const range = sheet.getRange(`${ref}:${endCell}`);
    range.values = values;
    await context.sync();
  });
}

export async function insertFormula(
  cell: string,
  formula: string,
): Promise<void> {
  // The Office JS API always expects English-locale formulas (commas as
  // separators). LLMs sometimes produce European-locale formulas that use
  // semicolons — normalise them here.
  const normalised = formula.replace(/;/g, ",");
  await runInExcel(async (context) => {
    const {sheetName, ref} = parseSheetRef(cell);
    const sheet = getSheet(context, sheetName);
    const range = sheet.getRange(ref);
    range.formulas = [[normalised]];
    await context.sync();
  });
}

export interface ChartStyleOptions {
  title?: string;
  colorScheme?: string;
  dataLabels?: boolean;
  legend?: {
    position?: string;
    visible?: boolean;
  };
  axisTitles?: {
    x?: string;
    y?: string;
  };
}

export interface CreateChartOptions extends ChartStyleOptions {
  type: string;
  dataRange: string;
  sheetName?: string;
  /**
   * Where to place the chart on the sheet, as a range ("G1:O15") or a single
   * top-left anchor cell ("G1"). Supplied by the LLM so multiple charts don't
   * stack on top of each other. When omitted, the chart is auto-staggered
   * based on how many charts already exist on the sheet.
   */
  position?: string;
}

/** Falls back to a sensible non-default look when the LLM omits style fields. */
const DEFAULT_COLOR_SCHEME = "colorful";
const DEFAULT_DATA_LABELS = true;

/**
 * Chart types with no axes — assigning axis titles to these throws.
 * Compared as strings since this module is also imported outside Excel.run
 * (by the docs generator), where the `Excel` global doesn't exist.
 */
function isAxisless(chartType: Excel.ChartType): boolean {
  return chartType === "Pie" || chartType === "Doughnut";
}

export async function createChart(options: CreateChartOptions): Promise<void> {
  await runInExcel(async (context) => {
    // Support sheet name in dataRange (e.g. "Sheet1!A1:B5") or sheetName option
    const {sheetName: rangeSheet, ref} = parseSheetRef(options.dataRange);
    const targetSheet = options.sheetName || rangeSheet;
    const sheet = getSheet(context, targetSheet);

    const dataRange = sheet.getRange(ref);
    const chartType = mapChartType(options.type);

    // Resolve where the chart should sit. The LLM is responsible for passing a
    // position so charts don't stack on top of each other; when it doesn't,
    // fall back to auto-staggering by the number of existing charts.
    let anchors: {start: string; end: string};
    if (options.position && options.position.trim()) {
      anchors = resolveChartPosition(options.position);
    } else {
      const existing = sheet.charts;
      existing.load("count");
      await context.sync();
      anchors = defaultChartAnchors(existing.count);
    }

    const chart = sheet.charts.add(
      chartType,
      dataRange,
      Excel.ChartSeriesBy.auto,
    );

    chart.setPosition(anchors.start, anchors.end);
    await applyChartStyle(chart, chartType, options, context);

    await context.sync();
  });
}

export interface FormatChartOptions extends ChartStyleOptions {
  /** Name of an existing chart, e.g. "Chart 1" (as shown in the sheet layout markers). */
  chartName: string;
  sheetName?: string;
}

export async function formatChart(options: FormatChartOptions): Promise<void> {
  await runInExcel(async (context) => {
    const sheet = getSheet(context, options.sheetName);
    const chart = sheet.charts.getItemOrNullObject(options.chartName);
    chart.load("isNullObject,chartType");
    await context.sync();

    if (chart.isNullObject) {
      throw new Error(
        `No chart named "${options.chartName}" was found${options.sheetName ? ` on sheet "${options.sheetName}"` : ""}.`,
      );
    }

    await applyChartStyle(
      chart,
      chart.chartType as Excel.ChartType,
      options,
      context,
    );
    await context.sync();
  });
}

/**
 * Applies title/data-labels/legend/axis-titles/colour-scheme to a chart,
 * shared by both chart creation and restyling an existing chart.
 */
async function applyChartStyle(
  chart: Excel.Chart,
  chartType: Excel.ChartType,
  style: ChartStyleOptions,
  context: Excel.RequestContext,
): Promise<void> {
  if (style.title) {
    chart.title.text = style.title;
  }

  chart.dataLabels.showValue = style.dataLabels ?? DEFAULT_DATA_LABELS;

  if (style.legend?.visible === false) {
    chart.legend.visible = false;
  } else {
    chart.legend.position = mapLegendPosition(style.legend?.position);
  }

  // Pie/doughnut charts have no axes — assigning axis titles on them throws.
  if (!isAxisless(chartType)) {
    if (style.axisTitles?.x) {
      chart.axes.categoryAxis.title.text = style.axisTitles.x;
    }
    if (style.axisTitles?.y) {
      chart.axes.valueAxis.title.text = style.axisTitles.y;
    }
  }

  await applyColorScheme(
    chart,
    style.colorScheme ?? DEFAULT_COLOR_SCHEME,
    chartType,
    context,
  );
}

/**
 * Excel's JS API has no built-in "colour scheme" setter, so palettes are
 * applied by hand: per-point for single-series charts (pie/doughnut, or any
 * chart with exactly one series) so each category gets a distinct colour,
 * per-series otherwise. Line/scatter series are recoloured via their line
 * format since a fill colour has no visible effect on them.
 */
const PALETTES: Record<string, string[]> = {
  colorful: ["#4472C4", "#ED7D31", "#A5A5A5", "#FFC000", "#5B9BD5", "#70AD47"],
  blue: ["#1F4E79", "#2E75B6", "#5B9BD5", "#9DC3E6", "#BDD7EE", "#DEEBF7"],
  green: ["#375623", "#548235", "#70AD47", "#A9D18E", "#C6E0B4", "#E2EFDA"],
  orange: ["#833C00", "#C55A11", "#ED7D31", "#F4B183", "#F8CBAD", "#FCE4D6"],
  grayscale: ["#262626", "#404040", "#595959", "#7F7F7F", "#A6A6A6", "#D9D9D9"],
};

async function applyColorScheme(
  chart: Excel.Chart,
  scheme: string,
  chartType: Excel.ChartType,
  context: Excel.RequestContext,
): Promise<void> {
  const palette =
    PALETTES[scheme.toLowerCase()] ?? PALETTES[DEFAULT_COLOR_SCHEME];
  const isLineLike =
    chartType === Excel.ChartType.line ||
    chartType === Excel.ChartType.xyscatter;

  const series = chart.series;
  series.load("count");
  await context.sync();

  if (series.count === 0) return;

  if (series.count === 1) {
    // Single-series chart: colour each category/point distinctly.
    const points = series.getItemAt(0).points;
    points.load("count");
    await context.sync();
    for (let i = 0; i < points.count; i++) {
      points
        .getItemAt(i)
        .format.fill.setSolidColor(palette[i % palette.length]);
    }
  } else {
    for (let i = 0; i < series.count; i++) {
      const color = palette[i % palette.length];
      const s = series.getItemAt(i);
      if (isLineLike) {
        s.format.line.color = color;
      } else {
        s.format.fill.setSolidColor(color);
      }
    }
  }
}

function mapLegendPosition(position?: string): Excel.ChartLegendPosition {
  const map: Record<string, Excel.ChartLegendPosition> = {
    top: Excel.ChartLegendPosition.top,
    bottom: Excel.ChartLegendPosition.bottom,
    left: Excel.ChartLegendPosition.left,
    right: Excel.ChartLegendPosition.right,
  };
  return map[(position ?? "").toLowerCase()] ?? Excel.ChartLegendPosition.right;
}

/**
 * Resolves an LLM-supplied chart position into start/end anchor cells for
 * Chart.setPosition. Accepts either a full range ("G1:O15") or a single
 * top-left cell ("G1"), in which case a default 8-column × 15-row span is used.
 */
function resolveChartPosition(position: string): {
  start: string;
  end: string;
} {
  const {ref} = parseSheetRef(position.trim());
  if (ref.includes(":")) {
    const [start, end] = ref.split(":");
    return {start: start.trim(), end: end.trim()};
  }
  return {start: ref, end: offsetCell(ref, 14, 8)};
}

/**
 * Computes non-overlapping default anchors for a chart, staggered vertically
 * by how many charts already exist on the sheet. Prevents charts from being
 * placed on top of each other when no explicit position is supplied.
 */
function defaultChartAnchors(existingCount: number): {
  start: string;
  end: string;
} {
  const topRow = 1 + existingCount * 16;
  return {start: `G${topRow}`, end: `O${topRow + 14}`};
}

/** Default Excel column width, in points (8.43 chars ≈ 64px ≈ 48pt). */
const DEFAULT_COL_WIDTH_POINTS = 48;
/** Default Excel row height, in points, used when standardHeight is unknown. */
const DEFAULT_ROW_HEIGHT_POINTS = 15;

/**
 * Describes the approximate cell area each existing chart occupies, so the LLM
 * can avoid placing new charts on top of them. Chart geometry is reported by
 * Office.js in points from the worksheet origin; we convert to A1-style cell
 * anchors using the sheet's row height and the default column width. This is
 * exact on a default grid and approximate when columns/rows are resized.
 */
function summarizeCharts(
  charts: Excel.Chart[],
  standardRowHeightPoints: number,
): string {
  if (!charts || charts.length === 0) return "";

  const rowH =
    standardRowHeightPoints > 0
      ? standardRowHeightPoints
      : DEFAULT_ROW_HEIGHT_POINTS;
  const colW = DEFAULT_COL_WIDTH_POINTS;

  const descriptions = charts.map((c) => {
    const startCol = Math.max(0, Math.round(c.left / colW));
    const startRow = Math.max(0, Math.round(c.top / rowH));
    const endCol = Math.max(
      startCol,
      Math.round((c.left + c.width) / colW) - 1,
    );
    const endRow = Math.max(
      startRow,
      Math.round((c.top + c.height) / rowH) - 1,
    );
    const startRef = `${colToString(startCol + 1)}${startRow + 1}`;
    const endRef = `${colToString(endCol + 1)}${endRow + 1}`;
    const name = c.name || "chart";
    return `"${name}" ~ ${startRef}:${endRef}`;
  });

  return `[Existing charts (approx. cell area — do NOT overlap these): ${descriptions.join("; ")}]`;
}

/** The size, in bytes, of what's actually sent — not `.length`, which counts UTF-16 units. */
function byteLength(text: string): number {
  return new TextEncoder().encode(text).length;
}

/** Strips a leading "Sheet!" prefix from an address: "Sheet1!A1:D3" → "A1:D3". */
function stripSheetPrefix(address: string): string {
  const bangIndex = address.indexOf("!");
  return bangIndex === -1 ? address : address.slice(bangIndex + 1);
}

/**
 * Lists the cells in a range that contain formulas, so the LLM can review and
 * CORRECT them. The CSV only shows computed values (including error values like
 * "#DIV/0!"), never the underlying formula. `formulas` is a 2D array where a
 * cell holding a formula is a string starting with "="; plain-value cells hold
 * their value instead.
 */
export function formulasListing(
  formulas: unknown[][],
  address: string,
): string {
  if (!formulas || formulas.length === 0) return "";

  const rangeRef = address.includes("!") ? address.split("!")[1] : address;
  const startCell = rangeRef.split(":")[0];
  const match = startCell.match(/^([A-Z]+)(\d+)$/i);
  if (!match) return "";

  const startCol = colFromString(match[1].toUpperCase());
  const startRow = parseInt(match[2], 10);

  const lines: string[] = [];
  const MAX = 300;
  for (let r = 0; r < formulas.length; r++) {
    const row = formulas[r] ?? [];
    for (let c = 0; c < row.length; c++) {
      const f = row[c];
      if (typeof f === "string" && f.startsWith("=")) {
        lines.push(`${colToString(startCol + c)}${startRow + r}: ${f}`);
        if (lines.length >= MAX) {
          lines.push("... [more formulas omitted]");
          return `Formulas (actual formula text — CSV above shows only their results):\n${lines.join("\n")}`;
        }
      }
    }
  }

  if (lines.length === 0) return "";
  return `Formulas (actual formula text — CSV above shows only their results):\n${lines.join("\n")}`;
}

export async function applySort(
  rangeAddress: string,
  column: number,
  ascending: boolean,
): Promise<void> {
  await runInExcel(async (context) => {
    const {sheetName, ref} = parseSheetRef(rangeAddress);
    const sheet = getSheet(context, sheetName);
    const range = sheet.getRange(ref);
    range.sort.apply([{key: column, ascending}]);
    await context.sync();
  });
}

export async function applyFilter(
  rangeAddress: string,
  column: number,
  criteria: string,
): Promise<void> {
  await runInExcel(async (context) => {
    const {sheetName, ref} = parseSheetRef(rangeAddress);
    const sheet = getSheet(context, sheetName);
    const range = sheet.getRange(ref);
    // Use worksheet-level autoFilter
    sheet.autoFilter.apply(range, column, {
      filterOn: Excel.FilterOn.custom,
      criterion1: criteria,
    });
    await context.sync();
  });
}

export interface FormatCellsOptions {
  range: string;
  fillColor?: string;
  fontColor?: string;
  bold?: boolean;
  italic?: boolean;
  fontSize?: number;
  /** Excel number format code applied to the whole range, e.g. "#,##0.00". */
  numberFormat?: string;
}

export async function formatCells(options: FormatCellsOptions): Promise<void> {
  await runInExcel(async (context) => {
    const {sheetName, ref} = parseSheetRef(options.range);
    const sheet = getSheet(context, sheetName);
    const range = sheet.getRange(ref);

    // A number format must be a 2D array matching the range dimensions, so
    // read the range size first when a format was requested.
    if (options.numberFormat) {
      range.load(["rowCount", "columnCount"]);
      await context.sync();
    }

    if (options.fillColor) {
      range.format.fill.color = options.fillColor;
    }
    if (options.fontColor) {
      range.format.font.color = options.fontColor;
    }
    if (options.bold !== undefined) {
      range.format.font.bold = options.bold;
    }
    if (options.italic !== undefined) {
      range.format.font.italic = options.italic;
    }
    if (options.fontSize !== undefined) {
      range.format.font.size = options.fontSize;
    }
    if (options.numberFormat) {
      const numberFormat = options.numberFormat;
      range.numberFormat = Array.from({length: range.rowCount}, () =>
        Array.from({length: range.columnCount}, () => numberFormat),
      );
    }

    await context.sync();
  });
}

export async function toggleGridlines(show: boolean): Promise<void> {
  await runInExcel(async (context) => {
    const sheet = context.workbook.worksheets.getActiveWorksheet();
    sheet.showGridlines = show;
    await context.sync();
  });
}

export async function createSheet(name: string): Promise<void> {
  await runInExcel(async (context) => {
    const sheet = context.workbook.worksheets.add(name);
    sheet.activate();
    await context.sync();
  });
}

/** A script that hasn't returned by this point can no longer hang the turn. */
const SCRIPT_TIMEOUT_MS = 20_000;
/** Return values are size-capped before going back to the model, same spirit as getContextData. */
const SCRIPT_RESULT_MAX_CHARS = 4_000;
/** Longer strings are summarised by length rather than quoted to the user. */
const SCRIPT_SUMMARY_MAX_CHARS = 80;
const SCRIPT_SUMMARY_MAX_FIELDS = 5;

const AsyncFunction = Object.getPrototypeOf(async function () {})
  .constructor as new (
  ...args: string[]
) => (context: Excel.RequestContext) => Promise<unknown>;

/**
 * Runs model-generated Office.js — the `run_script` escape hatch. `code` is
 * the body of an async function that receives `context` already inside an
 * active Excel.run batch, and may `return` a value to report back.
 *
 * A hung script can't hang the turn: the promise races a timeout, though the
 * underlying Excel.run call (having no cancellation hook) keeps running in
 * the background.
 */
export async function runScript(code: string): Promise<unknown> {
  await validateScript(code);
  let scriptFn: (context: Excel.RequestContext) => Promise<unknown>;
  try {
    scriptFn = new AsyncFunction("context", code);
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    throw new Error(`Script has a syntax error: ${msg}`);
  }

  const run = runInExcel(async (context) => {
    const returnValue = await scriptFn(context);
    await context.sync();
    return returnValue;
  });

  const timeout = new Promise<never>((_, reject) => {
    setTimeout(
      () =>
        reject(
          new Error(`Script timed out after ${SCRIPT_TIMEOUT_MS / 1000}s.`),
        ),
      SCRIPT_TIMEOUT_MS,
    );
  });

  return Promise.race([run, timeout]);
}

/**
 * Serialises a script's return value for the model, capped in size. Returns
 * undefined for scripts that returned nothing, so callers can omit the
 * "Result: ..." suffix entirely.
 *
 * Dates become ISO strings and non-finite numbers keep their names, both of
 * which JSON.stringify would otherwise turn into something the model would
 * read as a real value. Excel error values arrive as strings ("#DIV/0!")
 * and pass through untouched.
 */
export function serializeScriptResult(value: unknown): string | undefined {
  if (value === undefined) return undefined;

  let text: string;
  try {
    text =
      typeof value === "string"
        ? value
        : JSON.stringify(normalizeForJson(value), null, 2);
  } catch {
    text = String(value);
  }

  if (text.length > SCRIPT_RESULT_MAX_CHARS) {
    return (
      text.slice(0, SCRIPT_RESULT_MAX_CHARS) +
      `\n... [truncated: showing the first ${SCRIPT_RESULT_MAX_CHARS} of ${text.length} characters — return less data, or narrow the query]`
    );
  }
  return text;
}

/**
 * One line describing what a script returned, for the user. The model gets
 * the value itself via serializeScriptResult; the user gets its shape, so a
 * thousand-row query doesn't dump a thousand rows into the chat.
 */
export function summarizeScriptResult(value: unknown): string | undefined {
  if (value === undefined) return undefined;
  if (value === null) return "returned nothing";

  if (Array.isArray(value)) {
    const firstRow = value[0];
    if (Array.isArray(firstRow)) {
      return `returned ${value.length} ${plural(value.length, "row")} × ${firstRow.length} ${plural(firstRow.length, "column")}`;
    }
    return `returned a list of ${value.length} ${plural(value.length, "item")}`;
  }

  if (typeof value === "string") {
    return value.length <= SCRIPT_SUMMARY_MAX_CHARS
      ? `returned "${value}"`
      : `returned ${value.length} characters of text`;
  }

  if (value instanceof Date) return `returned ${value.toISOString()}`;

  if (typeof value === "object") {
    const keys = Object.keys(value as Record<string, unknown>);
    if (keys.length === 0) return "returned an empty object";
    const shown = keys.slice(0, SCRIPT_SUMMARY_MAX_FIELDS).join(", ");
    const rest = keys.length - SCRIPT_SUMMARY_MAX_FIELDS;
    return `returned an object with: ${shown}${rest > 0 ? ` and ${rest} more` : ""}`;
  }

  return `returned ${String(value)}`;
}

function plural(count: number, word: string): string {
  return count === 1 ? word : `${word}s`;
}

/**
 * Replaces the values JSON.stringify would mangle: Dates (which it renders
 * unlabelled) and NaN/Infinity (which it renders as null).
 */
function normalizeForJson(
  value: unknown,
  seen = new WeakSet<object>(),
): unknown {
  if (value instanceof Date) return value.toISOString();
  if (typeof value === "number" && !Number.isFinite(value))
    return String(value);
  if (typeof value === "bigint") return value.toString();
  if (value === null || typeof value !== "object") return value;

  if (seen.has(value)) return "[circular]";
  seen.add(value);

  if (Array.isArray(value)) return value.map((v) => normalizeForJson(v, seen));

  const out: Record<string, unknown> = {};
  for (const [key, v] of Object.entries(value as Record<string, unknown>)) {
    out[key] = normalizeForJson(v, seen);
  }
  return out;
}

// ---- Utilities ----

/**
 * Parses a cell/range reference that may include a sheet name prefix.
 * E.g. "Germany!A1" → { sheetName: "Germany", ref: "A1" }
 *      "A1:D3"      → { sheetName: undefined, ref: "A1:D3" }
 *      "'My Sheet'!B2" → { sheetName: "My Sheet", ref: "B2" }
 */
function parseSheetRef(address: string): {
  sheetName: string | undefined;
  ref: string;
} {
  const bangIndex = address.indexOf("!");
  if (bangIndex === -1) return {sheetName: undefined, ref: address};
  let sheetName = address.slice(0, bangIndex);
  const ref = address.slice(bangIndex + 1);
  // Remove surrounding quotes if present: 'My Sheet' → My Sheet
  if (sheetName.startsWith("'") && sheetName.endsWith("'")) {
    sheetName = sheetName.slice(1, -1);
  }
  return {sheetName, ref};
}

/**
 * Returns the correct worksheet — uses the named sheet if the reference
 * includes a "SheetName!" prefix, otherwise falls back to the active sheet.
 */
function getSheet(
  context: Excel.RequestContext,
  sheetName: string | undefined,
): Excel.Worksheet {
  return sheetName
    ? context.workbook.worksheets.getItem(sheetName)
    : context.workbook.worksheets.getActiveWorksheet();
}

/**
 * Converts a 2D values array into a CSV-like string with cell addresses.
 * The address parameter is like "Sheet1!A2:C3". Output looks like:
 *
 *   ,A,B,C
 *   2,,morango,maças
 *   3,0,5,10
 *
 * This lets the LLM know exactly which cell each value lives in.
 */
export function valuesToAddressedCsv(
  values: unknown[][],
  address: string,
): string {
  // Office.js can report a used range (isNullObject: false) yet still return
  // null for its values — seen with some unusually large or external ranges.
  if (!values || values.length === 0) return "";

  // Parse starting cell from address like "Sheet1!B2:C3" or "B2:C3"
  const rangeRef = address.includes("!") ? address.split("!")[1] : address;
  const startCell = rangeRef.split(":")[0];
  const match = startCell.match(/^([A-Z]+)(\d+)$/i);

  if (!match) {
    // Fallback: plain CSV if we can't parse
    return valuesToCsv(values);
  }

  const startCol = colFromString(match[1].toUpperCase());
  const startRow = parseInt(match[2], 10);

  // Build column headers
  const colHeaders: string[] = [];
  for (let c = 0; c < (values[0]?.length ?? 0); c++) {
    colHeaders.push(colToString(startCol + c));
  }

  const lines: string[] = [];
  // Header row: empty cell + column letters
  lines.push("," + colHeaders.join(","));

  // Data rows: row number + values
  for (let r = 0; r < values.length; r++) {
    const rowNum = startRow + r;
    const cells = values[r].map((cell) => {
      const s = String(cell ?? "");
      return s.includes(",") || s.includes('"') || s.includes("\n")
        ? `"${s.replace(/"/g, '""')}"`
        : s;
    });
    lines.push(rowNum + "," + cells.join(","));
  }

  return lines.join("\n");
}

function valuesToCsv(values: unknown[][]): string {
  return values
    .map((row) =>
      row
        .map((cell) => {
          const s = String(cell ?? "");
          return s.includes(",") || s.includes('"') || s.includes("\n")
            ? `"${s.replace(/"/g, '""')}"`
            : s;
        })
        .join(","),
    )
    .join("\n");
}

function offsetCell(
  cell: string,
  rowOffset: number,
  colOffset: number,
): string {
  const match = cell.match(/^([A-Z]+)(\d+)$/i);
  if (!match) return cell;
  const col = match[1].toUpperCase();
  const row = parseInt(match[2], 10);
  const newCol = colToString(colFromString(col) + colOffset);
  const newRow = row + rowOffset;
  return `${newCol}${newRow}`;
}

function colFromString(col: string): number {
  let n = 0;
  for (let i = 0; i < col.length; i++) {
    n = n * 26 + (col.charCodeAt(i) - 64);
  }
  return n;
}

function colToString(n: number): string {
  let s = "";
  while (n > 0) {
    const rem = (n - 1) % 26;
    s = String.fromCharCode(65 + rem) + s;
    n = Math.floor((n - 1) / 26);
  }
  return s;
}

function mapChartType(type: string): Excel.ChartType {
  const map: Record<string, Excel.ChartType> = {
    bar: Excel.ChartType.barClustered,
    column: Excel.ChartType.columnClustered,
    line: Excel.ChartType.line,
    pie: Excel.ChartType.pie,
    area: Excel.ChartType.area,
    scatter: Excel.ChartType.xyscatter,
    doughnut: Excel.ChartType.doughnut,
  };
  return map[type.toLowerCase()] ?? Excel.ChartType.columnClustered;
}
